import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import { IFacility } from '../../models/Facility';
import { IOnlineUser } from '../../models/OnlineUser';
import './EnvelopeDisplay.css';

interface IEnvelopeDisplayProps {
  user: IOnlineUser;
  inmate: any;
  facility: IFacility;
}

const EnvelopeDisplay: React.FC<IEnvelopeDisplayProps> = ({
  user,
  inmate,
  facility
}) => {
  return (
    <>
      <div className="hero bg-primary text-light py-5">
        <Container className="pt-3">
          <div className="row">
            <div className="col-lg-8 pt-2">
              <h1>Mail Instructions</h1>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-5">
        <Alert variant="warning" className="mb-4">
          <Alert.Heading className="h5 mb-2">
            <strong>Important:</strong>
          </Alert.Heading>
          Your mail must be addressed exactly as shown below or it will not be delivered.
        </Alert>

        {/* Desktop/Large Display */}
        <div className="envelope-preview bg-light border border-2 rounded-3 p-3 mb-5 d-none d-lg-block">
          <div className="virtual-envelope bg-white border rounded-3 shadow-sm position-relative mx-auto">
            <div className="return-address">
              <div className="address-content">
                <p className="address-line mb-1">{user.firstName} {user.lastName} #{user.id}</p>
                <p className="address-line mb-1">{user.address}</p>
                {user.address2 && <p className="address-line mb-1">{user.address2}</p>}
                <p className="address-line mb-1">{user.city}, {user.state} {user.zip}</p>
                <p className="address-line mb-0">Phone: {user.phoneNumber}</p>
              </div>
            </div>

            <div className="stamp-area border border-2 border-secondary d-flex align-items-center justify-content-center text-muted">
              <small>Stamp<br />Here</small>
            </div>

            <div className="destination-address text-center">
              <div className="address-content">
                <p className="address-line mb-1">{inmate.firstName} {inmate.lastName} ({inmate.account})</p>
                <p className="address-line mb-1">{facility.name}</p>
                <p className="address-line mb-1">PO Box 12345</p>
                <p className="address-line mb-0">Grand Forks, ND 58201</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Display */}
        <div className="mobile-address-cards d-lg-none mb-5">
          <Card className="mb-4 shadow-sm">
            <Card.Body className="p-3">
              <div className="d-flex align-items-start mb-3">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                     style={{width: "30px", height: "30px", minWidth: "30px"}}>
                  <small>1</small>
                </div>
                <div>
                  <h5 className="text-primary font-weight-bold ml-2">Your Return Address</h5>
                  <p className="text-muted font-size-6 mb-0">Write this in the top left corner of your envelope</p>
                </div>
              </div>
              <div className="address-content bg-light p-3 rounded border-start border-primary border-4">
                <p className="address-line mb-1">{user.firstName} {user.lastName} #{user.id}</p>
                <p className="address-line mb-1">{user.address}</p>
                {user.address2 && <p className="address-line mb-1">{user.address2}</p>}
                <p className="address-line mb-1">{user.city}, {user.state} {user.zip}</p>
                <p className="address-line mb-0">Phone: {user.phoneNumber}</p>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm">
            <Card.Body className="p-3">
              <div className="d-flex align-items-start mb-3">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                     style={{width: "30px", height: "30px", minWidth: "30px"}}>
                  <small>2</small>
                </div>
                <div>
                  <h5 className="text-primary font-weight-bold ml-2">Inmate Address</h5>
                  <p className="text-muted font-size-6 mb-0">Write this in the center of your envelope</p>
                </div>
              </div>
              <div className="address-content bg-light p-3 rounded border-start border-primary border-4">
                <p className="address-line mb-1">{inmate.firstName} {inmate.lastName} (#{inmate.account})</p>
                <p className="address-line mb-1">{facility.name}</p>
                <p className="address-line mb-1">PO Box 12345</p>
                <p className="address-line mb-0">Grand Forks, ND 58201</p>
              </div>
            </Card.Body>
          </Card>
        </div>

        <Card className="shadow-sm">
          <Card.Body className="p-4">
            <Card.Title className="text-center mb-4 h3 text-primary">
              Additional Instructions
            </Card.Title>

            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="instruction-item bg-light rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                         style={{width: "30px", height: "30px", minWidth: "30px"}}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <span className="font-size-5 ml-2">Use black or blue ink only</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="instruction-item bg-light rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                         style={{width: "30px", height: "30px", minWidth: "30px"}}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <span className="font-size-5 ml-2">Write clearly and legibly</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="instruction-item bg-light rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                         style={{width: "30px", height: "30px", minWidth: "30px"}}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <span className="font-size-5 ml-2">Include your return address exactly as shown above</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="instruction-item bg-light rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                         style={{width: "30px", height: "30px", minWidth: "30px"}}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <span className="font-size-5 ml-2">Do not include any contraband or prohibited items</span>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="instruction-item bg-light rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                         style={{width: "30px", height: "30px", minWidth: "30px"}}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <span className="font-size-5 ml-2">All mail is subject to inspection</span>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default EnvelopeDisplay;
